import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en',
        debug: false,
        interpolation: {
            escapeValue: false,
        },
        resources: {
            en: {
                translation: {
                    nav: {
                        home: 'Home',
                        about: 'About',
                        services: 'Services',
                        projects: 'Projects',
                        reviews: 'Reviews',
                        contact: 'Contact',
                        start: 'Start Project'
                    },
                    hero: {
                        badge: 'The future of tech starts here',
                        title: 'We Build Digital Authority Through Innovation',
                        sub: 'Enterprise-grade software, cloud infrastructure, and AI solutions tailored for your business.',
                        cta: 'Order Development',
                        cases: 'Our Cases'
                    },
                    about: {
                        tag: 'About Us',
                        title: 'Who We Are',
                        text: 'We are Nexova Dev LLC, providing IT solutions for all industries. We also offer App & Web Development.',
                        learn: 'Learn More',
                        stats: {
                            projects: 'Completed projects',
                            years: 'Years on market',
                            clients: 'Happy clients',
                            support: '24/7 Support'
                        },
                        story: {
                            title: 'Our Journey',
                            text: 'Founded with a vision to revolutionize the digital landscape, Nexova Dev began as a small collective of passionate developers. Today, we stand as a global leader in IT solutions, empowering businesses to thrive in the modern world.'
                        },
                        team: {
                            title: 'Meet the Minds',
                            roles: {
                                founder: 'Founder',
                                cofounder: 'Co-Founder',
                                cto: 'CTO',
                                lead: 'Lead Designer',
                                head: 'Head of Product',
                                engineer: 'Software Engineer'
                            }
                        },
                        tech: {
                            title: 'Our Technology Stack'
                        },
                        mission: {
                            title: 'Our Mission',
                            text: 'To empower businesses with cutting-edge digital solutions that drive global progress. We don\'t just build software; we architect the future.'
                        },
                        values: {
                            title: 'Our Core Values',
                            v1: { t: 'Innovation', d: 'We embrace emerging technologies to solve tomorrow\'s challenges today.' },
                            v2: { t: 'Reliability', d: 'Enterprise-grade stability and zero-tolerance for failure in architecture.' },
                            v3: { t: 'Transparency', d: 'Open communication and complete visibility throughout the development lifecycle.' },
                            v4: { t: 'Scalability', d: 'Architecting solutions that grow seamlessly with your business ambitions.' },
                            v5: { t: 'Security', d: 'Implementing military-grade protection for your most valuable digital assets.' },
                            v6: { t: 'Excellence', d: 'A relentless pursuit of perfection in every line of code we write.' }
                        },
                        milestones: [
                            { year: '2016', t: 'The Beginning', d: 'Nexova Dev founded as a boutique development studio.' },
                            { year: '2019', t: 'Global Expansion', d: 'Opened our first international office and expanded to cloud services.' },
                            { year: '2022', t: 'AI Integration', d: 'Launched our dedicated AI & Machine Learning research division.' },
                            { year: '2025', t: 'Digital Authority', d: 'Recognized as a leading provider of enterprise-grade IT solutions.' }
                        ]
                    },
                    services: {
                        tag: 'Service',
                        title: 'Our Tech Services',
                        s1: {
                            t: 'Custom Software',
                            d: 'Developing complex backend systems and mission-critical applications with a focus on security.',
                            features: ['Microservices Architecture', 'High-Load Systems', 'Legacy Modernization', 'API Integration']
                        },
                        s2: {
                            t: 'Web & Mobile',
                            d: 'High-performance interfaces that engage users and drive conversions across all devices.',
                            features: ['React / Next.js', 'React Native / iOS / Android', 'PWA Development', 'UI/UX Design']
                        },
                        s3: {
                            t: 'AI & Machine Learning',
                            d: 'Implementing LLMs and neural networks to automate workflows and data analysis.',
                            features: ['NLP & Chatbots', 'Computer Vision', 'Predictive Analytics', 'Automated Reporting']
                        },
                        s4: {
                            t: 'Cloud Solutions',
                            d: 'Secure and scalable cloud infrastructure to support your growing business needs.',
                            features: ['AWS / GCP / Azure', 'DevOps & CI/CD', 'Serverless Architecture', 'Cost Optimization']
                        },
                        s5: {
                            t: 'Data Analytics',
                            d: 'Transforming raw data into actionable insights for better decision making.',
                            features: ['Big Data Processing', 'BI Dashboards', 'Data Warehousing', 'Real-time Analytics']
                        },
                        s6: {
                            t: 'Cybersecurity',
                            d: 'Comprehensive security assessments and protection for your digital assets.',
                            features: ['Penetration Testing', 'Security Audits', 'Compliance (GDPR/HIPAA)', 'Threat Detection']
                        },
                        process: {
                            title: 'Our Process',
                            steps: [
                                { t: 'Discovery', d: 'We analyze your business goals and technical requirements.' },
                                { t: 'Design', d: 'Creating intuitive UI/UX and scalable system architecture.' },
                                { t: 'Development', d: 'Agile coding with regular updates and feedback loops.' },
                                { t: 'Launch', d: 'Deployment, testing, and continuous support.' }
                            ]
                        }
                    },
                    projects: {
                        tag: 'Portfolio',
                        title: 'Our Best Projects',
                        back: 'Back to Projects',
                        date: 'Date',
                        client: 'Client',
                        own_products_title: 'Our Products',
                        client_projects_title: 'Client Projects',
                        challenge: {
                            title: 'The Challenge',
                            text: 'The primary challenge was to build a scalable system capable of handling millions of requests per second with minimal latency.'
                        },
                        solution: {
                            title: 'The Solution',
                            text: 'We implemented a microservices architecture using Kubernetes and optimized data processing algorithms for maximum efficiency.'
                        },
                        result: {
                            title: 'The Result',
                            text: 'The project resulted in a 40% performance increase and a significant reduction in the customer\'s operating costs.'
                        },
                        cta: {
                            title: 'Have a Great Project In Mind?',
                            sub: 'Let\'s turn your ideas into reality with cutting-edge technology.'
                        },
                        items: [
                            {
                                title: 'Xperia Hotels',
                                slug: 'xperia-hotels',
                                desc: 'Comprehensive hotel management system with guest applications.',
                                fullDesc: 'A robust hotel management platform designed for luxury resorts. It features a custom booking engine, real-time inventory management, localized guest applications for mobile devices, and an integrated staff dashboard for seamless operations and enhanced guest satisfaction.',
                                category: 'Mobile & Web',
                                img: '/images/portfolio/xperia.png'
                            },
                            {
                                title: 'Arbitrage Bot',
                                slug: 'arbitrage-bot',
                                desc: 'High-speed crypto trading automation with real-time exchange monitoring.',
                                fullDesc: 'A sophisticated algorithmic trading system that scans multiple cryptocurrency exchanges simultaneously. Built for micro-second latency, it identifies price discrepancies and executes trades automatically, featuring advanced risk management and real-time performance analytics.',
                                category: 'Fintech',
                                img: '/images/portfolio/bot.png'
                            },
                            {
                                title: 'NextGen ERP',
                                slug: 'nextgen-erp',
                                desc: 'Scale-ready enterprise resource planning infrastructure for global logistics.',
                                fullDesc: 'Enterprise-grade ERP system tailored for international logistics and supply chain management. It integrates warehouse management, fleet tracking, and financial reporting into a unified cloud-native platform, capable of handling complex cross-border operations at scale.',
                                category: 'Cloud',
                                img: '/images/portfolio/cloud.png'
                            },
                            {
                                title: 'VisionAI',
                                slug: 'vision-ai',
                                desc: 'AI-driven object recognition system for industrial floor automation.',
                                fullDesc: 'Advanced computer vision solution for smart manufacturing. This system utilizes deep learning models to identify parts, detect defects, and optimize robot movements on factory floors in real-time, significantly reducing human error and increasing production speed.',
                                category: 'AI',
                                img: '/images/portfolio/ai.png'
                            },
                            {
                                title: 'Retail Premium',
                                slug: 'retail-premium',
                                desc: 'High-end e-commerce platform with immersive UI and seamless ux.',
                                fullDesc: 'A luxury digital storefront designed for premium brands. It offers a fully immersive shopping experience with high-fidelity visuals, AI-powered product recommendations, and a highly optimized checkout flow that ensures maximum conversion and customer loyalty.',
                                category: 'E-commerce',
                                img: '/images/portfolio/retail.png'
                            },
                            {
                                title: 'DataNexus',
                                slug: 'data-nexus',
                                desc: 'Big Data visualization engine for real-time market behavior analysis.',
                                fullDesc: 'DataNexus is a comprehensive analytics platform that processes terabytes of market data in real-time, providing actionable insights through advanced neural network visualizations and predictive modelling to help financial institutions make data-driven decisions.',
                                category: 'Analytics',
                                img: '/images/portfolio/analytics.png'
                            },
                            {
                                title: 'Solaris Web3',
                                slug: 'solaris-web3',
                                desc: 'Next-generation blockchain explorer with deep liquidity insights.',
                                fullDesc: 'A cutting-edge Web3 indexing and visualization tool. Solaris provides deep insights into decentralized finance protocols, tracking liquidity flows, smart contract interactions, and market sentiment across multiple blockchain networks with enterprise-grade reliability.',
                                category: 'Web3',
                                img: '/images/portfolio/web3.png'
                            },
                            {
                                title: 'HealthSync App',
                                slug: 'healthsync-app',
                                desc: 'Cross-platform mobile application for real-time patient monitoring.',
                                fullDesc: 'HealthSync App revolutionizes patient care by connecting wearable devices directly to hospital systems, allowing doctors to monitor vital signs in real-time and receive automated alerts for critical changes in patient health, improving response times and outcomes.',
                                category: 'Mobile',
                                img: '/images/portfolio/mobile.png'
                            },
                            {
                                title: 'SecureNodes',
                                slug: 'secure-nodes',
                                desc: 'Decentralized server monitoring and threat detection system.',
                                fullDesc: 'A military-grade cybersecurity platform that monitors decentralized infrastructure. It uses behavioral analysis and AI to detect potential threats and unauthorized access attempts in real-time, providing comprehensive security for mission-critical server networks.',
                                category: 'Cybersecurity',
                                img: '/images/portfolio/security.png'
                            }
                        ]
                    },
                    reviews: {
                        tag: 'Reputation',
                        title: 'What Clients Say'
                    },
                    contact: {
                        tag: 'Connection',
                        title: 'Let\'s Create Something Great',
                        sub: 'Ready to discuss? Leave a request and our experts will contact you within 24 hours.',
                        address: '30 N Gould St, Ste R, Sheridan, WY 82801, USA',
                        form: {
                            title: 'Send us a Message',
                            subtitle: 'We usually respond within 24 hours.',
                            name: 'Your Name',
                            email: 'Email',
                            subject: 'Project Subject',
                            message: 'Tell us about your idea...',
                            send: 'Send Request',
                            working: 'Working Hours',
                            hours: 'Mon - Fri: 9:00 AM - 6:00 PM EST',
                            response: 'Average response time: < 2 hours'
                        }
                    },
                    footer: {
                        tagline: 'We strive for excellence in every line of code, creating solutions that change the world.',
                        nav: 'Navigation',
                        tech: 'Tech',
                        support: 'Support',
                        privacy: 'Privacy Policy'
                    },
                    privacy: {
                        title: 'Privacy Policy',
                        lastUpdated: 'Last Updated: February 14, 2026',
                        intro: 'At Nexova Dev LLC, we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you as to how we look after your personal data when you visit our website (nexovadev.com) and tell you about your privacy rights and how the law protects you.',
                        collection: {
                            title: 'Information We Collect',
                            text: 'We may collect, use, store and transfer different kinds of personal data about you which we have grouped together follows: Identity Data (name), Contact Data (email, address), and Technical Data (IP address, browser type).'
                        },
                        use: {
                            title: 'How We Use Your Information',
                            text: 'We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances: Where we need to perform the contract we are about to enter into or have entered into with you; Where it is necessary for our legitimate interests.'
                        },
                        retention: {
                            title: 'Data Retention',
                            text: 'We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.'
                        },
                        security: {
                            title: 'Data Security',
                            text: 'We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorized way, altered or disclosed.'
                        },
                        rights: {
                            title: 'Your Legal Rights',
                            text: 'Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to request access, correction, erasure, restriction, transfer, to object to processing, to portability of data and (where the lawful ground of processing is consent) to withdraw consent.'
                        },
                        cookies: {
                            title: 'Cookies',
                            text: 'We use cookies and similar tracking technologies to track the activity on our Request Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.'
                        },
                        thirdParty: {
                            title: 'Third-Party Links',
                            text: 'This website may include links to third-party websites, plug-ins and applications. Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements.'
                        },
                        contact: {
                            title: 'Contact Us',
                            text: 'If you have any questions about this privacy policy or our privacy practices, please contact us at: contact@nexovadev.com or at our office: 30 N Gould St, Ste R, Sheridan, WY 82801, USA.'
                        },
                    },
                    faq: {
                        title: 'Frequently Asked Questions',
                        categories: [
                            {
                                title: '🛠️ General & Services',
                                items: [
                                    {
                                        q: 'What services does Nexova Dev provide?',
                                        a: 'We are a full-service software studio specializing in high-performance Web and Native Mobile applications. From initial concept and UI/UX design to full-stack development and cloud integration, we build scalable digital products tailored to your business goals.'
                                    },
                                    {
                                        q: 'Do you build for both iOS and Android?',
                                        a: 'Yes. We specialize in Native Development using SwiftUI (iOS) and Kotlin (Android) to ensure the highest performance, smoothest animations, and best user experience. We also develop robust web platforms to ensure your business is accessible everywhere.'
                                    },
                                    {
                                        q: 'What industries do you specialize in?',
                                        a: 'While we are tech-agnostic, we have deep expertise in Hospitality (custom hotel management systems) and Service Marketplaces. Our current projects, like HotelApp and Nexfixer, showcase our ability to handle complex booking logic and multi-role user platforms.'
                                    },
                                    {
                                        q: 'Do you provide UI/UX design services?',
                                        a: 'Yes, design is a core part of our process. We create intuitive, user-centric interfaces and high-fidelity prototypes before starting any development to ensure the final product meets your vision.'
                                    }
                                ]
                            },
                            {
                                title: '🚀 Process & Logistics',
                                items: [
                                    {
                                        q: 'How long does it take to build an app or website?',
                                        a: 'Timelines vary based on complexity. A standard MVP (Minimum Viable Product) typically takes 8–12 weeks. More complex systems, like an integrated hotel management platform, may take longer. We provide a detailed roadmap after our initial discovery phase.'
                                    },
                                    {
                                        q: 'Will I own the source code after the project is finished?',
                                        a: 'Absolutely. Once the final payment is made, all intellectual property rights and source code are transferred to you. We believe in transparency and ensuring our clients have full control over their digital assets.'
                                    },
                                    {
                                        q: 'How do we communicate during the development process?',
                                        a: 'We prioritize transparency. You will have a dedicated point of contact and regular updates via your preferred channel (Slack, Telegram, or Email). We also provide scheduled demos at the end of each development sprint so you can see the progress in real-time.'
                                    },
                                    {
                                        q: 'Can you work with an existing codebase?',
                                        a: 'Definitely. We frequently assist clients in scaling or refactoring existing applications. After a thorough code audit, we can provide a plan for modernization, bug fixing, or feature expansion.'
                                    }
                                ]
                            },
                            {
                                title: '🔒 Security & Support',
                                items: [
                                    {
                                        q: 'How do you handle data security?',
                                        a: 'Security is baked into our code from day one. We use industry-standard encryption, secure API protocols, and reliable cloud infrastructures like Firebase or AWS to ensure your user data and business logic are protected against vulnerabilities.'
                                    },
                                    {
                                        q: 'Do you provide maintenance after the launch?',
                                        a: 'Yes. Technology moves fast, and we don\'t just "disappear" after launch. We offer ongoing maintenance packages to ensure your app stays compatible with the latest iOS/Android updates and to help you ship new features as your business grows.'
                                    },
                                    {
                                        q: 'Where will my data and application be hosted?',
                                        a: 'We typically recommend and set up hosting on major cloud providers like AWS, Google Cloud, or Firebase. However, we are flexible and can work with your preferred hosting provider or on-premise infrastructure if required.'
                                    }
                                ]
                            }
                        ]
                    },
                    tech: {
                        react: {
                            title: 'React / Next.js Development',
                            desc: 'Building high-performance, SEO-friendly web applications with the latest React ecosystem.',
                            features: ['Server-Side Rendering (SSR)', 'Core Web Vitals Optimization', 'Component-Based Architecture', 'Progressive Web Apps (PWA)', 'Custom State Management', 'Internationalization (i18n)', 'Headless CMS Integration', 'Enterprise Security Standards'],
                            benefits: ['Lightning-fast performance', 'Excellent SEO visibility', 'Interactive & responsive UI', 'Highly maintainable code', 'Seamless complex data flow', 'Global market reach', 'Effortless content management', 'Secure user data protection'],
                            image: '/images/tech/react-hero.png'
                        },
                        node: {
                            title: 'Node.js Backend',
                            desc: 'Scalable, event-driven backend systems powered by Node.js and NestJS.',
                            features: ['Microservices Architecture', 'Real-time WebSockets', 'High-concurrency Handling', 'REST & GraphQL APIs', 'Microservices Orchestration', 'Real-time Data Streams', 'Database Query Optimization', 'Automated Testing Suites'],
                            benefits: ['Low latency response', 'Rapid development cycles', 'Cost-effective scaling', 'Robust ecosystem integration', 'Elastic horizontal scalability', 'Instant user notifications', 'High-speed data retrieval', 'Zero-bug production environment'],
                            image: '/images/tech/node-hero.png'
                        },
                        ai: {
                            title: 'AI & Machine Learning',
                            desc: 'Leveraging LLMs, neural networks, and computer vision to automate specific business processes.',
                            features: ['Custom LLM Training', 'Predictive Analytics', 'Computer Vision Systems', 'Natural Language Processing', 'Custom LLM Fine-tuning', 'Image & Video Recognition', 'Sentiment Analysis Systems', 'AI Recommendation Engines'],
                            benefits: ['Automated decision making', 'Reduced operational costs', 'Enhanced user personalization', 'Data-driven business growth', 'Industry-specific intelligence', 'Automated visual inspection', 'Deep customer feedback insights', 'Personalization that drives sales'],
                            image: '/images/tech/ai-hero.png'
                        },
                        cloud: {
                            title: 'Cloud Infrastructure',
                            desc: 'Secure, scalable cloud architecture on AWS, Google Cloud, and Azure with Kubernetes orchestration.',
                            features: ['Kubernetes & Docker', 'Serverless Computing', 'CI/CD Pipelines', 'Infrastructure as Code', 'Multi-region Load Balancing', 'Managed Security & WAF', 'Cost-efficient Serverless', 'Automated CI/CD Pipelines'],
                            benefits: ['Zero-downtime deployments', 'Automatic elastic scaling', 'Disaster recovery solutions', 'Top-tier security compliance', 'High availability & disaster recovery', 'Military-grade threat protection', 'Pay-per-execution savings', 'Rapid & safe code deployment'],
                            image: '/images/tech/cloud-hero.png'
                        }
                    }
                }
            },
            tr: {
                translation: {
                    nav: {
                        home: 'Anasayfa',
                        about: 'Hakkımızda',
                        services: 'Hizmetler',
                        projects: 'Projeler',
                        reviews: 'Yorumlar',
                        contact: 'İletişim',
                        start: 'Projeye Başla'
                    },
                    hero: {
                        badge: 'Teknolojinin geleceği burada başlıyor',
                        title: 'İnovasyon Yoluyla Dijital Otorite İnşa Ediyoruz',
                        sub: 'İşletmeniz için özel olarak tasarlanmış kurumsal düzeyde yazılım, bulut altyapısı ve yapay zeka çözümleri.',
                        cta: 'Geliştirme Sipariş Et',
                        cases: 'Vakalarımız'
                    },
                    about: {
                        tag: 'Hakkımızda',
                        title: 'Biz Kimiz',
                        text: 'Biz, tüm sektörler için BT çözümleri sunan Nexova Dev LLC şirketiyiz. Ayrıca Uygulama ve Web Geliştirme hizmetleri de sağlıyoruz.',
                        learn: 'Daha Fazla Bilgi',
                        stats: {
                            projects: 'Tamamlanan projeler',
                            years: 'Yıldır piyasada',
                            clients: 'Mutlu müşteri',
                            support: '24/7 Destek'
                        },
                        story: {
                            title: 'Yolculuğumuz',
                            text: 'Dijital dünyada devrim yaratma vizyonuyla kurulan Nexova Dev, tutkulu geliştiricilerden oluşan küçük bir kolektif olarak başladı. Bugün, işletmelerin modern dünyada gelişmesini sağlayan BT çözümlerinde küresel bir lider olarak duruyoruz.'
                        },
                        team: {
                            title: 'Takımla Tanışın',
                            roles: {
                                founder: 'Kurucu',
                                cofounder: 'Kurucu Ortak',
                                cto: 'CTO',
                                lead: 'Tasarım Lideri',
                                head: 'Ürün Başkanı',
                                engineer: 'Yazılım Mühendisi'
                            }
                        },
                        tech: {
                            title: 'Teknoloji Yığınımız'
                        },
                        mission: {
                            title: 'Misyonumuz',
                            text: 'İşletmeleri küresel ilerlemeyi sağlayan en son dijital çözümlerle güçlendirmek. Biz sadece yazılım yapmıyoruz; geleceği mimar ediyoruz.'
                        },
                        values: {
                            title: 'Temel Değerlerimiz',
                            v1: { t: 'İnovasyon', d: 'Yarının zorluklarını bugünden çözmek için gelişen teknolojileri benimsiyoruz.' },
                            v2: { t: 'Güvenilirlik', d: 'Kurumsal düzeyde kararlılık ve mimaride hataya sıfır tolerans.' },
                            v3: { t: 'Şeffaflık', d: 'Geliştirme yaşam döngüsü boyunca açık iletişim ve tam görünürlük.' },
                            v4: { t: 'Ölçeklenebilirlik', d: 'İş amaçlarınızla sorunsuz bir şekilde büyüyen çözümler tasarlamak.' },
                            v5: { t: 'Güvenlik', d: 'En değerli dijital varlıklarınız için askeri düzeyde koruma uygulamak.' },
                            v6: { t: 'Mükemmellik', d: 'Yazdığımız her kod satırında amansız bir mükemmellik arayışı.' }
                        },
                        milestones: [
                            { year: '2016', t: 'Başlangıç', d: 'Nexova Dev butik bir geliştirme stüdyosu olarak kuruldu.' },
                            { year: '2019', t: 'Küresel Genişleme', d: 'İlk uluslararası ofisimizi açtık ve bulut hizmetlerine genişledik.' },
                            { year: '2022', t: 'Yapay Zeka Entegrasyonu', d: 'Özel Yapay Zeka ve Makine Öğrenimi araştırma bölümümüzü başlattık.' },
                            { year: '2025', t: 'Dijital Otorite', d: 'Kurumsal düzeyde BT çözümlerinin lider sağlayıcısı olarak tanındık.' }
                        ]
                    },
                    services: {
                        tag: 'Hizmet',
                        title: 'Teknoloji Hizmetlerimiz',
                        s1: {
                            t: 'Özel Yazılım',
                            d: 'Güvenliğe odaklanan karmaşık arka uç sistemleri ve kritik öneme sahip uygulamalar geliştirme.',
                            features: ['Mikroservis Mimarisi', 'Yüksek Yüklü Sistemler', 'Eski Sistem Modernizasyonu', 'API Entegrasyonu']
                        },
                        s2: {
                            t: 'Web ve Mobil',
                            d: 'Kullanıcıları cezbeden ve tüm cihazlarda dönüşüm sağlayan yüksek performanslı arayüzler.',
                            features: ['React / Next.js', 'React Native / iOS / Android', 'PWA Geliştirme', 'UI/UX Tasarımı']
                        },
                        s3: {
                            t: 'Yapay Zeka ve Makine Öğrenimi',
                            d: 'İş akışlarını ve veri analizini otomatikleştirmek için LLM\'lerin ve sinir ağlarının uygulanması.',
                            features: ['NLP ve Sohbet Botları', 'Bilgisayarlı Görü', 'Tahmine Dayalı Analiz', 'Otomatik Raporlama']
                        },
                        s4: {
                            t: 'Bulut Çözümleri',
                            d: 'Büyüyen iş ihtiyaçlarınızı desteklemek için güvenli ve ölçeklenebilir bulut altyapısı.',
                            features: ['AWS / GCP / Azure', 'DevOps ve CI/CD', 'Sunucusuz Mimari', 'Maliyet Optimizasyonu']
                        },
                        s5: {
                            t: 'Veri Analitiği',
                            d: 'Daha iyi karar verme için ham verileri eyleme dönüştürülebilir içgörülere dönüştürme.',
                            features: ['Büyük Veri İşleme', 'BI Panoları', 'Veri Ambarı', 'Gerçek Zamanlı Analitik']
                        },
                        s6: {
                            t: 'Siber Güvenlik',
                            d: 'Dijital varlıklarınız için kapsamlı güvenlik değerlendirmeleri ve koruma.',
                            features: ['Sızma Testi', 'Güvenlik Denetimleri', 'Uyumluluk (GDPR/HIPAA)', 'Tehdit Algılama']
                        },
                        process: {
                            title: 'Sürecimiz',
                            steps: [
                                { t: 'Keşif', d: 'İş hedeflerinizi ve teknik gereksinimlerinizi analiz ediyoruz.' },
                                { t: 'Tasarım', d: 'Sezgisel UI/UX ve ölçeklenebilir sistem mimarisi oluşturma.' },
                                { t: 'Geliştirme', d: 'Düzenli güncellemeler ve geri bildirim döngüleri ile çevik kodlama.' },
                                { t: 'Lansman', d: 'Dağıtım, test ve sürekli destek.' }
                            ]
                        }
                    },
                    projects: {
                        tag: 'Portföy',
                        title: 'En İyi Projelerimiz',
                        back: 'Projelere Dön',
                        date: 'Tarih',
                        client: 'Müşteri',
                        own_products_title: 'Ürünlerimiz',
                        client_projects_title: 'Müşteri Projeleri',
                        challenge: {
                            title: 'Zorluk',
                            text: 'Temel zorluk, saniyede milyonlarca isteği minimum gecikmeyle işleyebilen ölçeklenebilir bir sistem oluşturmaktı.'
                        },
                        solution: {
                            title: 'Çözüm',
                            text: 'Kubernetes kullanarak mikro hizmet mimarisini uyguladık ve maksimum verimlilik için veri işleme algoritmalarını optimize ettik.'
                        },
                        result: {
                            title: 'Sonuç',
                            text: 'Proje, performansta %40 artış ve müşterinin işletme maliyetlerinde önemli bir azalma ile sonuçlandı.'
                        },
                        cta: {
                            title: 'Aklınızda Harika Bir Proje mi Var?',
                            sub: 'En son teknolojilerle fikirlerinizi gerçeğe dönüştürelim.'
                        },
                        items: [
                            {
                                title: 'Xperia Hotels',
                                slug: 'xperia-hotels',
                                desc: 'Misafir uygulamaları ile kapsamlı otel yönetim sistemi.',
                                fullDesc: 'Lüks resortlar için tasarlanmış sağlam bir otel yönetim platformu. Özel rezervasyon motoru, gerçek zamanlı envanter yönetimi, mobil cihazlar için yerelleştirilmiş misafir uygulamaları ve kesintisiz operasyonlar için entegre bir personel paneli içerir.',
                                category: 'Mobil ve Web',
                                img: '/images/portfolio/xperia.png'
                            },
                            {
                                title: 'Arbitraj Botu',
                                slug: 'arbitrage-bot',
                                desc: 'Gerçek zamanlı borsa izleme özelliği ile yüksek hızlı kripto ticaret otomasyonu.',
                                fullDesc: 'Birden fazla kripto para borsasını aynı anda tarayan gelişmiş bir algoritmik ticaret sistemi. Mikrosaniye gecikme süresi için tasarlanmış olup, fiyat farklılıklarını tespit eder ve işlemleri otomatik olarak gerçekleştirir.',
                                category: 'Fintek',
                                img: '/images/portfolio/bot.png'
                            },
                            {
                                title: 'NextGen ERP',
                                slug: 'nextgen-erp',
                                desc: 'Küresel lojistik için ölçeklenmeye hazır kurumsal kaynak planlama altyapısı.',
                                fullDesc: 'Uluslararası lojistik ve tedarik zinciri yönetimi için kurumsal düzeyde ERP sistemi. Depo yönetimi, filo takibi ve finansal raporlamayı birleşik bir bulut yerel platformunda birleştirerek karmaşık sınır ötesi operasyonları yönetir.',
                                category: 'Bulut',
                                img: '/images/portfolio/cloud.png'
                            },
                            {
                                title: 'VisionAI',
                                slug: 'vision-ai',
                                desc: 'Endüstriyel zemin otomasyonu için yapay zeka destekli nesne tanıma sistemi.',
                                fullDesc: 'Akıllı üretim için gelişmiş bilgisayarlı görü çözümü. Bu sistem, parçaları tanımlamak, hataları tespit etmek ve fabrika zeminindeki robot hareketlerini optimize etmek için derin öğrenme modellerini kullanır.',
                                category: 'YZ',
                                img: '/images/portfolio/ai.png'
                            },
                            {
                                title: 'Retail Premium',
                                slug: 'retail-premium',
                                desc: 'Sürükleyici kullanıcı arayüzü ve sorunsuz kullanıcı deneyimine sahip üst düzey e-ticaret platformu.',
                                fullDesc: 'Premium markalar için tasarlanmış lüks bir dijital mağaza. Yüksek kaliteli görseller, yapay zeka destekli ürün önerileri ve maksimum dönüşüm sağlayan optimize edilmiş bir ödeme akışı sunar.',
                                category: 'E-ticaret',
                                img: '/images/portfolio/retail.png'
                            },
                            {
                                title: 'DataNexus',
                                slug: 'data-nexus',
                                desc: 'Gerçek zamanlı piyasa davranışı analizi için Büyük Veri görselleştirme motoru.',
                                fullDesc: 'DataNexus, terabaytlarca piyasa verisini gerçek zamanlı olarak işleyen, gelişmiş sinir ağı görselleştirmeleri ve öngörücü modelleme yoluyla uygulanabilir içgörüler sağlayan kapsamlı bir analitik platformudur.',
                                category: 'Analitik',
                                img: '/images/portfolio/analytics.png'
                            },
                            {
                                title: 'Solaris Web3',
                                slug: 'solaris-web3',
                                desc: 'Derin likidite içgörülerine sahip yeni nesil blok zinciri gezgini.',
                                fullDesc: 'Yeni nesil bir Web3 indeksleme ve görselleştirme aracı. Solaris, merkezi olmayan finans protokolleri hakkında derin içgörüler sunarak likidite akışlarını ve akıllı sözleşme etkileşimlerini takip eder.',
                                category: 'Web3',
                                img: '/images/portfolio/web3.png'
                            },
                            {
                                title: 'HealthSync Mobil',
                                slug: 'healthsync-app',
                                desc: 'Gerçek zamanlı hasta izleme için platformlar arası mobil uygulama.',
                                fullDesc: 'HealthSync App, giyilebilir cihazları doğrudan hastane sistemlerine bağlayarak doktorların hayati belirtileri gerçek zamanlı olarak izlemesine ve kritik değişiklikler için otomatik uyarılar almasına olanak tanır.',
                                category: 'Mobil',
                                img: '/images/portfolio/mobile.png'
                            },
                            {
                                title: 'SecureNodes',
                                slug: 'secure-nodes',
                                desc: 'Merkezi olmayan sunucu izleme ve tehdit algılama sistemi.',
                                fullDesc: 'Merkezi olmayan altyapıyı izleyen askeri düzeyde bir siber güvenlik platformu. Potansiyel tehditleri ve yetkisiz erişim girişimlerini tespit etmek için davranış analizi ve yapay zeka kullanır.',
                                category: 'Siber Güvenlik',
                                img: '/images/portfolio/security.png'
                            }
                        ]
                    },
                    reviews: {
                        tag: 'İtibar',
                        title: 'Müşteriler Ne Diyor'
                    },
                    contact: {
                        tag: 'Bağlantı',
                        title: 'Harika Bir Şey Yaratalım',
                        sub: 'Görüşmeye hazır mısınız? Bir istek bırakın, uzmanlarımız 24 saat içinde sizinle iletişime geçecektir.',
                        address: '30 N Gould St, Ste R, Sheridan, WY 82801, USA',
                        form: {
                            title: 'Bize Mesaj Gönderin',
                            subtitle: 'Genellikle 24 saat içinde cevap veriyoruz.',
                            name: 'Adınız',
                            email: 'E-posta',
                            subject: 'Proje Konusu',
                            message: 'Fikrinizden bahsedin...',
                            send: 'İstek Gönder',
                            working: 'Çalışma Saatleri',
                            hours: 'Pzt - Cum: 09:00 - 18:00',
                            response: 'Ortalama yanıt süresi: < 2 saat'
                        }
                    },
                    footer: {
                        tagline: 'Dünyayı değiştiren teknolojik çözümler yaratarak her kod satırında mükemmellik için çabalıyoruz.',
                        nav: 'Navigasyon',
                        tech: 'Teknoloji',
                        support: 'Destek',
                        privacy: 'Gizlilik Politikası'
                    },
                    privacy: {
                        title: 'Gizlilik Politikası',
                        lastUpdated: 'Son Güncelleme: 14 Şubat 2026',
                        intro: 'Nexova Dev LLC olarak gizliliğinize saygı duyuyor ve kişisel verilerinizi korumayı taahhüt ediyoruz. Bu gizlilik politikası, web sitemizi (nexovadev.com) ziyaret ettiğinizde kişisel verilerinizi nasıl işlediğimiz ve gizlilik haklarınız ile yasaların sizi nasıl koruduğu hakkında sizi bilgilendirecektir.',
                        collection: {
                            title: 'Topladığımız Bilgiler',
                            text: 'Hakkınızda farklı kişisel veri türlerini toplayabilir, kullanabilir, saklayabilir ve aktarabiliriz: Kimlik Verileri (isim), İletişim Verileri (e-posta, adres) ve Teknik Veriler (IP adresi, tarayıcı tipi).'
                        },
                        use: {
                            title: 'Bilgilerinizi Nasıl Kullanıyoruz',
                            text: 'Kişisel verilerinizi yalnızca yasaların izin verdiği durumlarda kullanacağız. En yaygın olarak, kişisel verilerinizi şu durumlarda kullanacağız: Sizinle yapacağımız veya yapmış olduğumuz sözleşmeyi ifa etmemiz gerektiğinde; Meşru menfaatlerimiz için gerekli olduğunda.'
                        },
                        retention: {
                            title: 'Veri Saklama',
                            text: 'Kişisel verilerinizi, yasal, muhasebe veya raporlama gerekliliklerini yerine getirmek de dahil olmak üzere, topladığımız amaçları yerine getirmek için gerekli olduğu sürece saklayacağız.'
                        },
                        security: {
                            title: 'Veri Güvenliği',
                            text: 'Kişisel verilerinizin kazara kaybolmasını, yetkisiz bir şekilde kullanılmasını veya bunlara erişilmesini, değiştirilmesini veya ifşa edilmesini önlemek için uygun güvenlik önlemleri aldık.'
                        },
                        rights: {
                            title: 'Yasal Haklarınız',
                            text: 'Belirli durumlarda, kişisel verilerinizle ilgili olarak veri koruma yasaları kapsamında haklarınız vardır; bunlara erişim, düzeltme, silme, kısıtlama, transfer talep etme, işlemeye itiraz etme, veri taşınabilirliği ve (işlemenin yasal zemini rıza ise) rızayı geri çekme hakkı dahildir.'
                        },
                        cookies: {
                            title: 'Çerezler',
                            text: 'Hizmetimizdeki etkinliği izlemek ve belirli bilgileri tutmak için çerezleri ve benzer izleme teknolojilerini kullanıyoruz. Tarayıcınıza tüm çerezleri reddetmesi veya bir çerez gönderildiğinde bunu belirtmesi talimatını verebilirsiniz.'
                        },
                        thirdParty: {
                            title: 'Üçüncü Taraf Bağlantıları',
                            text: 'Bu web sitesi üçüncü taraf web sitelerine, eklentilerine ve uygulamalarına bağlantılar içerebilir. Bu bağlantılara tıklamak veya bu bağlantıları etkinleştirmek, üçüncü tarafların sizin hakkınızda veri toplamasına veya paylaşmasına izin verebilir. Biz bu üçüncü taraf web sitelerini kontrol etmiyoruz ve gizlilik bildirimlerinden sorumlu değiliz.'
                        },
                        contact: {
                            title: 'Bize Ulaşın',
                            text: 'Bu gizlilik politikası veya gizlilik uygulamalarımız hakkında sorularınız varsa, lütfen bizimle iletişime geçin: contact@nexovadev.com veya ofisimizden: 30 N Gould St, Ste R, Sheridan, WY 82801, USA.'
                        },
                    },
                    faq: {
                        title: 'Sıkça Sorulan Sorular',
                        categories: [
                            {
                                title: '🛠️ Genel ve Hizmetler',
                                items: [
                                    {
                                        q: 'Nexova Dev hangi hizmetleri sunuyor?',
                                        a: 'Yüksek performanslı Web ve Yerel Mobil uygulamalar konusunda uzmanlaşmış tam hizmet veren bir yazılım stüdyosuyuz. İlk konsept ve UI/UX tasarımından tam yığın geliştirmeye ve bulut entegrasyonuna kadar, iş hedeflerinize göre uyarlanmış ölçeklenebilir dijital ürünler oluşturuyoruz.'
                                    },
                                    {
                                        q: 'Hem iOS hem de Android için geliştirme yapıyor musunuz?',
                                        a: 'Evet. En yüksek performansı, en akıcı animasyonları ve en iyi kullanıcı deneyimini sağlamak için SwiftUI (iOS) ve Kotlin (Android) kullanarak Yerel Geliştirme konusunda uzmanlaştık. Ayrıca işletmenizin her yerden erişilebilir olmasını sağlamak için sağlam web platformları geliştiriyoruz.'
                                    },
                                    {
                                        q: 'Hangi sektörlerde uzmanlaşmış durumdasınız?',
                                        a: 'Teknoloji bağımsız olsak da, Konaklama (özel otel yönetim sistemleri) ve Hizmet Pazaryerleri konusunda derin uzmanlığa sahibiz. HotelApp ve Nexfixer gibi mevcut projelerimiz, karmaşık rezervasyon mantığını ve çok rollü kullanıcı platformlarını yönetme yeteneğimizi sergilemektedir.'
                                    },
                                    {
                                        q: 'UI/UX tasarım hizmeti veriyor musunuz?',
                                        a: 'Evet, tasarım sürecimizin temel bir parçasıdır. Geliştirmeye başlamadan önce son ürünün vizyonunuzu karşıladığından emin olmak için sezgisel, kullanıcı odaklı arayüzler ve yüksek kaliteli prototipler oluşturuyoruz.'
                                    }
                                ]
                            },
                            {
                                title: '🚀 Süreç ve Lojistik',
                                items: [
                                    {
                                        q: 'Bir uygulama veya web sitesi oluşturmak ne kadar sürer?',
                                        a: 'Zaman çizelgeleri karmaşıklığa bağlı olarak değişir. Standart bir MVP (Minimum Uygulanabilir Ürün) genellikle 8-12 hafta sürer. Entegre bir otel yönetim platformu gibi daha karmaşık sistemler daha uzun sürebilir. İlk keşif aşamamızdan sonra ayrıntılı bir yol haritası sunuyoruz.'
                                    },
                                    {
                                        q: 'Proje bittikten sonra kaynak koduna sahip olacak mıyım?',
                                        a: 'Kesinlikle. Son ödeme yapıldıktan sonra tüm fikri mülkiyet hakları ve kaynak kodu size devredilir. Şeffaflığa ve müşterilerimizin dijital varlıkları üzerinde tam kontrole sahip olmalarını sağlamaya inanıyoruz.'
                                    },
                                    {
                                        q: 'Geliştirme sürecinde nasıl iletişim kuruyoruz?',
                                        a: 'Şeffaflığa öncelik veriyoruz. Özel bir iletişim noktanız olacak ve tercih ettiğiniz kanal (Slack, Telegram veya E-posta) üzerinden düzenli güncellemeler alacaksınız. Ayrıca ilerlemeyi gerçek zamanlı olarak görebilmeniz için her geliştirme sprintinin sonunda planlı demolar sunuyoruz.'
                                    },
                                    {
                                        q: 'Mevcut bir kod tabanıyla çalışabilir misiniz?',
                                        a: 'Kesinlikle. Müşterilerimize mevcut uygulamalarını ölçeklendirme veya yeniden düzenleme konusunda sık часто yardımcı oluyoruz. Kapsamlı bir kod denetiminden sonra modernizasyon, hata düzeltme veya özellik genişletme için bir plan sunabiliriz.'
                                    }
                                ]
                            },
                            {
                                title: '🔒 Güvenlik ve Destek',
                                items: [
                                    {
                                        q: 'Veri güvenliğini nasıl sağlıyorsunuz?',
                                        a: 'Güvenlik, ilk günden itibaren kodumuza dahil edilir. Biz endüstri standardı şifreleme, güvenli API protokolleri ve Firebase veya AWS gibi güvenilir bulut altyapıları kullanıyoruz. Kullanıcı verilerinizin ve iş mantığınızın güvenlik açıklarına karşı korunmasını sağlıyoruz.'
                                    },
                                    {
                                        q: 'Lansman sonrası bakım sağlıyor musunuz?',
                                        a: 'Evet. Teknoloji hızlı ilerliyor ve lansmandan sonra öylece "kaybolmuyoruz". Uygulamanızın en son iOS/Android güncellemeleriyle uyumlu kalmasını sağlamak ve işiniz büyüdükçe yeni özellikler sunmanıza yardımcı olmak için sürekli bakım paketleri sunuyoruz.'
                                    },
                                    {
                                        q: 'Verilerim ve uygulamam nerede barındırılacak?',
                                        a: 'Genellikle AWS, Google Cloud veya Firebase gibi büyük bulut sağlayıcılarında kurulum yapmanızı öneriyoruz. Ancak esnek davranabiliriz ve tercih ettiğiniz barındırma sağlayıcısı veya gerekirse yerel altyapı ile çalışabiliriz.'
                                    }
                                ]
                            }
                        ]
                    },
                    tech: {
                        react: {
                            title: 'React / Next.js Geliştirme',
                            desc: 'En yeni React ekosistemi ile yüksek performanslı, SEO dostu web uygulamaları oluşturma.',
                            features: ['Sunucu Taraflı Oluşturma (SSR)', 'Core Web Vitals Optimizasyonu', 'Bileşen Tabanlı Mimari', 'Progresif Web Uygulamaları (PWA)', 'Özel Durum Yönetimi (Redux/Zustand)', 'Uluslararasılaştırma (i18n)', 'Headless CMS Entegrasyonu', 'Kurumsal Güvenlik Standartları'],
                            benefits: ['Yıldırım hızında performans', 'Mükemmel SEO görünürlüğü', 'Etkileşimli ve duyarlı UI', 'Yüksek düzeyde bakımı kolay kod', 'Sorunsuz karmaşık veri akışı', 'Küresel pazar erişimi', 'Zahmetsiz içerik yönetimi', 'Güvenli kullanıcı veri koruması'],
                            image: '/images/tech/react-hero.png'
                        },
                        node: {
                            title: 'Node.js Backend',
                            desc: 'Ölçeklenebilir, olay güdümlü arka uç sistemleri Node.js ve NestJS tarafından desteklenmektedir.',
                            features: ['Mikroservis Mimarisi', 'Gerçek zamanlı WebSockets', 'Yüksek Eşzamanlılık Yönetimi', 'REST ve GraphQL API\'leri', 'Mikroservis Orkestrasyonu', 'Gerçek Zamanlı Veri Akışları', 'Veritabanı Sorgu Optimizasyonu', 'Otomatik Test Paketleri'],
                            benefits: ['Düşük gecikmeli yanıt', 'Hızlı geliştirme döngüleri', 'Maliyet etkin ölçekleme', 'Sağlam ekosistem entegrasyonu', 'Esnek yatay ölçeklenebilirlik', 'Anında kullanıcı bildirimleri', 'Yüksek hızlı veri alımı', 'Sıfır hatalı üretim ortamı'],
                            image: '/images/tech/node-hero.png'
                        },
                        ai: {
                            title: 'Yapay Zeka ve Makine Öğrenimi',
                            desc: 'Belirli iş süreçlerini otomatikleştirmek için LLM\'ler, sinir ağları ve bilgisayarlı görüden yararlanma.',
                            features: ['Özel LLM Eğitimi', 'Tahminleyici Analitik', 'Bilgisayarlı Görü Sistemleri', 'Doğal Dil İşleme', 'Özel LLM İnce Ayarı', 'Görüntü ve Video Tanıma', 'Duygu Analizi Sistemleri', 'Yapay Zeka Öneri Motorları'],
                            benefits: ['Otomatik karar verme', 'Azalan operasyonel maliyetler', 'Gelişmiş kullanıcı kişiselleştirme', 'Veri odaklı iş büyümesi', 'Sektöre özel zeka', 'Otomatik görsel inceleme', 'Derin müşteri geri bildirimi içgörüleri', 'Satışları artıran kişiselleştirme'],
                            image: '/images/tech/ai-hero.png'
                        },
                        cloud: {
                            title: 'Bulut Altyapısı',
                            desc: 'Kubernetes orkestrasyonu ile AWS, Google Cloud ve Azure üzerinde güvenli, ölçeklenebilir bulut mimarisi.',
                            features: ['Kubernetes ve Docker', 'Sunucusuz Bilişim', 'CI/CD Boru Hatları', 'Kod Olarak Altyapı', 'Çok Bölgeli Yük Dengeleme', 'Yönetilen Güvenlik ve WAF', 'Maliyet Etkin Serverless', 'Otomatik CI/CD Boru Hatları'],
                            benefits: ['Sıfır kesinti süreli dağıtımlar', 'Otomatik esnek ölçekleme', 'Felaket kurtarma çözümleri', 'Üst düzey güvenlik uyumluluğu', 'Yüksek kullanılabilirlik ve felaket kurtarma', 'Askeri düzeyde tehdit koruması', 'Yürütme başına ödeme tasarrufu', 'Hızlı ve güvenli kod dağıtımı'],
                            image: '/images/tech/cloud-hero.png'
                        }
                    }
                }
            },
            es: {
                translation: {
                    nav: {
                        home: 'Inicio',
                        about: 'Nosotros',
                        services: 'Servicios',
                        projects: 'Proyectos',
                        reviews: 'Reseñas',
                        contact: 'Contacto',
                        start: 'Empezar Proyecto'
                    },
                    hero: {
                        badge: 'El futuro de la tecnología comienza aquí',
                        title: 'Construimos Autoridad Digital a través de la Innovación',
                        sub: 'Software de clase empresarial, infraestructura en la nube y soluciones de IA personalizadas para su negocio.',
                        cta: 'Solicitar Desarrollo',
                        cases: 'Nuestros Casos'
                    },
                    about: {
                        tag: 'Sobre Nosotros',
                        title: 'Quiénes Somos',
                        text: 'Somos Nexova Dev LLC, ofreciendo soluciones de TI para todas las industrias. También ofrecemos desarrollo de aplicaciones y web.',
                        learn: 'Saber Más',
                        stats: {
                            projects: 'Proyectos completados',
                            years: 'Años en el mercado',
                            clients: 'Clientes felices',
                            support: 'Soporte 24/7'
                        },
                        story: {
                            title: 'Nuestra Trayectoria',
                            text: 'Fundada con la visión de revolucionar el paisaje digital, Nexova Dev comenzó como un pequeño colectivo de desarrolladores apasionados. Hoy en día, somos un líder global en soluciones de TI, empoderando a las empresas para prosperar en el mundo moderno.'
                        },
                        team: {
                            title: 'Conozca a las Mentes',
                            roles: {
                                founder: 'Fundador',
                                cofounder: 'Co-fundador',
                                cto: 'CTO',
                                lead: 'Diseñador Principal',
                                head: 'Jefe de Producto',
                                engineer: 'Ingeniero de Software'
                            }
                        },
                        tech: {
                            title: 'Nuestro Stack Tecnológico'
                        },
                        mission: {
                            title: 'Nuestra Misión',
                            text: 'Empoderar a las empresas con soluciones digitales de vanguardia que impulsen el progreso global. No solo construimos software; arquitectamos el futuro.'
                        },
                        values: {
                            title: 'Nuestros Valores Fundamentales',
                            v1: { t: 'Innovación', d: 'Adoptamos tecnologías emergentes para resolver los desafíos del mañana hoy mismo.' },
                            v2: { t: 'Fiabilidad', d: 'Estabilidad de nivel empresarial y tolerancia cero ante fallos en la arquitectura.' },
                            v3: { t: 'Transparencia', d: 'Comunicación abierta y visibilidad completa durante todo el ciclo de vida del desarrollo.' },
                            v4: { t: 'Escalabilidad', d: 'Arquitectando soluciones que crecen sin problemas con sus ambiciones comerciales.' },
                            v5: { t: 'Seguridad', d: 'Implementando protección de grado militar para sus activos digitales más valiosos.' },
                            v6: { t: 'Excelencia', d: 'Una búsqueda implacable de la perfección en cada línea de código que escribimos.' }
                        },
                        milestones: [
                            { year: '2016', t: 'El Comienzo', d: 'Nexova Dev fundada como un estudio boutique de desarrollo.' },
                            { year: '2019', t: 'Expansión Global', d: 'Abrimos nuestra primera oficina internacional y nos expandimos a servicios en la nube.' },
                            { year: '2022', t: 'Integración de IA', d: 'Lanzamos nuestra división dedicada a la investigación de IA y Aprendizaje Automático.' },
                            { year: '2025', t: 'Autoridad Digital', d: 'Reconocidos como un proveedor líder de soluciones de TI de nivel empresarial.' }
                        ]
                    },
                    services: {
                        tag: 'Servicio',
                        title: 'Nuestros Servicios Tech',
                        s1: {
                            t: 'Software a Medida',
                            d: 'Desarrollando sistemas backend complejos y aplicaciones de misión crítica enfocadas en la seguridad.',
                            features: ['Arquitectura de Microservicios', 'Sistemas de Alta Carga', 'Modernización de Sistemas Heredados', 'Integración de API']
                        },
                        s2: {
                            t: 'Web y Móvil',
                            d: 'Interfaces de alto rendimiento que atraen a los usuarios e impulsan las conversiones en todos los dispositivos.',
                            features: ['React / Next.js', 'React Native / iOS / Android', 'Desarrollo PWA', 'Diseño UI/UX']
                        },
                        s3: {
                            t: 'IA y Aprendizaje Automático',
                            d: 'Implementando LLMs y redes neuronales para automatizar flujos de trabajo y análisis de datos.',
                            features: ['NLP y Chatbots', 'Visión por Computadora', 'Analítica Predictiva', 'Informes Automatizados']
                        },
                        s4: {
                            t: 'Soluciones en la Nube',
                            d: 'Infraestructura en la nube segura y escalable para apoyar sus crecientes necesidades comerciales.',
                            features: ['AWS / GCP / Azure', 'DevOps & CI/CD', 'Arquitectura Serverless', 'Optimización de Costos']
                        },
                        s5: {
                            t: 'Analítica de Datos',
                            d: 'Transformando datos brutos en información útil para una mejor toma de decisiones.',
                            features: ['Procesamiento de Big Data', 'Dashboards de BI', 'Almacenamiento de Datos', 'Analítica en Tiempo Real']
                        },
                        s6: {
                            t: 'Ciberseguridad',
                            d: 'Evaluaciones de seguridad integrales y protección para sus activos digitales.',
                            features: ['Pruebas de Penetración', 'Auditorías de Seguridad', 'Cumplimiento (GDPR/HIPAA)', 'Detección de Amenazas']
                        },
                        process: {
                            title: 'Nuestro Proceso',
                            steps: [
                                { t: 'Descubrimiento', d: 'Analizamos sus objetivos comerciales y requisitos técnicos.' },
                                { t: 'Diseño', d: 'Creación de UI/UX intuitivo y arquitectura de sistema escalable.' },
                                { t: 'Desarrollo', d: 'Codificación ágil con actualizaciones regulares y ciclos de retroalimentación.' },
                                { t: 'Lanzamiento', d: 'Despliegue, pruebas y soporte continuo.' }
                            ]
                        }
                    },
                    projects: {
                        tag: 'Portafolio',
                        title: 'Nuestros Mejores Proyectos',
                        back: 'Volver a Proyectos',
                        date: 'Fecha',
                        client: 'Cliente',
                        own_products_title: 'Nuestros Productos',
                        client_projects_title: 'Proyectos de Clientes',
                        challenge: {
                            title: 'El Desafío',
                            text: 'El desafío principal fue construir un sistema escalable capaz de manejar millones de solicitudes por segundo con una latencia mínima.'
                        },
                        solution: {
                            title: 'La Solución',
                            text: 'Implementamos una arquitectura de microservicios utilizando Kubernetes y optimizamos los algoritmos de procesamiento de datos para una eficiencia máxima.'
                        },
                        result: {
                            title: 'El Resultado',
                            text: 'El proyecto resultó en un aumento del rendimiento del 40% y una reducción significativa en los costos operativos del cliente.'
                        },
                        cta: {
                            title: '¿Tienes un Gran Proyecto en Mente?',
                            sub: 'Hagamos realidad tus ideas con tecnología de vanguardia.'
                        },
                        items: [
                            {
                                title: 'Xperia Hotels',
                                slug: 'xperia-hotels',
                                desc: 'Sistema integral de gestión hotelera con aplicaciones para huéspedes.',
                                category: 'Móvil y Web',
                                img: '/images/portfolio/xperia.png'
                            },
                            {
                                title: 'Arbitrage Bot',
                                slug: 'arbitrage-bot',
                                desc: 'Automatización de criptocomercio de alta velocidad con monitoreo de exchanges en tiempo real.',
                                category: 'Fintech',
                                img: '/images/portfolio/bot.png'
                            },
                            {
                                title: 'NextGen ERP',
                                slug: 'nextgen-erp',
                                desc: 'Infraestructura de planificación de recursos empresariales lista para escalar para logística global.',
                                category: 'Nube',
                                img: '/images/portfolio/cloud.png'
                            },
                            {
                                title: 'VisionAI',
                                slug: 'vision-ai',
                                desc: 'Sistema de reconocimiento de objetos impulsado por IA para la automatización industrial.',
                                category: 'IA',
                                img: '/images/portfolio/ai.png'
                            },
                            {
                                title: 'Retail Premium',
                                slug: 'retail-premium',
                                desc: 'Plataforma de comercio electrónico de alta gama con interfaz inmersiva y experiencia fluida.',
                                category: 'E-commerce',
                                img: '/images/portfolio/retail.png'
                            },
                            {
                                title: 'DataNexus',
                                slug: 'data-nexus',
                                desc: 'Motor de visualización de Big Data para el análisis del comportamiento del mercado en tiempo real.',
                                fullDesc: 'DataNexus es una plataforma de analítica integral que procesa terabytes de datos de mercado en tiempo real, proporcionando información útil a través de visualizaciones avanzadas de redes neuronales y modelado predictivo.',
                                category: 'Analítica',
                                img: '/images/portfolio/analytics.png'
                            },
                            {
                                title: 'Solaris Web3',
                                slug: 'solaris-web3',
                                desc: 'Explorador de blockchain de próxima generación con información profunda de liquidez.',
                                category: 'Web3',
                                img: '/images/portfolio/web3.png'
                            },
                            {
                                title: 'HealthSync App',
                                slug: 'healthsync-app',
                                desc: 'Aplicación móvil multiplataforma para el monitoreo de pacientes en tiempo real.',
                                fullDesc: 'HealthSync App revoluciona el cuidado del paciente conectando dispositivos portátiles directamente a los sistemas hospitalarios, permitiendo a los médicos monitorear los signos vitales en tiempo real y recibir alertas automáticas ante cambios críticos.',
                                category: 'Móvil',
                                img: '/images/portfolio/mobile.png'
                            },
                            {
                                title: 'SecureNodes',
                                slug: 'secure-nodes',
                                desc: 'Sistema descentralizado de monitoreo de servidores y detección de amenazas.',
                                category: 'Ciberseguridad',
                                img: '/images/portfolio/security.png'
                            }
                        ]
                    },
                    reviews: {
                        tag: 'Reputación',
                        title: 'Lo Que Dicen los Clientes'
                    },
                    contact: {
                        tag: 'Conexión',
                        title: 'Creemos Algo Grande',
                        sub: '¿Listo para discutirlo? Deje una solicitud y nuestros expertos le contactarán en menos de 24 horas.',
                        address: '30 N Gould St, Ste R, Sheridan, WY 82801, USA',
                        form: {
                            title: 'Envíenos un Mensaje',
                            subtitle: 'Normalmente respondemos en menos de 24 horas.',
                            name: 'Su Nombre',
                            email: 'Email',
                            subject: 'Asunto de Proyecto',
                            message: 'Cuéntenos su idea...',
                            send: 'Enviar Solicitud',
                            working: 'Horario Laboral',
                            hours: 'Lun - Vie: 9:00 AM - 6:00 PM EST',
                            response: 'Tiempo promedio de respuesta: < 2 horas'
                        }
                    },
                    footer: {
                        tagline: 'Nos esforzamos por la excelencia en cada línea de código, creando soluciones que cambian el mundo.',
                        nav: 'Navegación',
                        tech: 'Tecnología',
                        support: 'Soporte',
                        privacy: 'Política de Privacidad'
                    },
                    privacy: {
                        title: 'Política de Privacidad',
                        lastUpdated: 'Última actualización: 14 de febrero de 2026',
                        intro: 'En Nexova Dev LLC, respetamos su privacidad y nos comprometemos a proteger sus datos personales. Esta política de privacidad le informará sobre cómo cuidamos sus datos personales cuando visita nuestro sitio web (nexovadev.com) y le informará sobre sus derechos de privacidad y cómo la ley le protege.',
                        collection: {
                            title: 'Información que Recopilamos',
                            text: 'Podemos recopilar, usar, almacenar y transferir diferentes tipos de datos personales sobre usted, los cuales hemos agrupado de la siguiente manera: Datos de Identidad (nombre), Datos de Contacto (email, dirección) y Datos Técnicos (dirección IP, tipo de navegador).'
                        },
                        use: {
                            title: 'Cómo Usamos su Información',
                            text: 'Solo usaremos sus datos personales cuando la ley nos lo permita. Más comúnmente, usaremos sus datos personales en las siguientes circunstancias: cuando necesitemos ejecutar el contrato que estamos a punto de celebrar o hemos celebrado con usted; cuando sea necesario para nuestros intereses legítimos.'
                        },
                        retention: {
                            title: 'Retención de Datos',
                            text: 'Solo conservaremos sus datos personales durante el tiempo que sea necesario para cumplir con los fines para los que los recopilamos, incluyendo la satisfacción de cualquier requisito legal, contable o de informes.'
                        },
                        security: {
                            title: 'Seguridad de los Datos',
                            text: 'Hemos implementado medidas de seguridad adecuadas para evitar que sus datos personales se pierdan accidentalmente, se usen o se acceda a ellos de forma no autorizada, se alteren o se divulguen.'
                        },
                        rights: {
                            title: 'Sus Derechos Legales',
                            text: 'Bajo ciertas circunstancias, tiene derechos bajo las leyes de protección de datos en relación con sus datos personales, incluyendo el derecho a solicitar acceso, corrección, eliminación, restricción, transferencia, a oponerse al procesamiento, a la portabilidad de los datos y (donde la base legal sea el consentimiento) a retirar el consentimiento.'
                        },
                        cookies: {
                            title: 'Cookies',
                            text: 'Usamos cookies y tecnologías de seguimiento similares para rastrear la actividad en nuestro Servicio de Solicitud y mantener cierta información. Puede configurar su navegador para rechazar todas las cookies o para indicar cuando se envía una cookie.'
                        },
                        thirdParty: {
                            title: 'Enlaces de Terceros',
                            text: 'Este sitio web puede incluir enlaces a sitios web, complementos y aplicaciones de terceros. Hacer clic en esos enlaces o habilitar esas conexiones puede permitir que terceros recopilen o compartan datos sobre usted. No controlamos estos sitios web de terceros y no somos responsables de sus declaraciones de privacidad.'
                        },
                        contact: {
                            title: 'Contáctenos',
                            text: 'Si tiene alguna pregunta sobre esta política de privacidad o nuestras prácticas de privacidad, contáctenos en: contact@nexovadev.com o en nuestra oficina: 30 N Gould St, Ste R, Sheridan, WY 82801, USA.'
                        }
                    },
                    faq: {
                        title: 'Preguntas Frecuentes',
                        categories: [
                            {
                                title: '🛠️ General y Servicios',
                                items: [
                                    {
                                        q: '¿Qué servicios ofrece Nexova Dev?',
                                        a: 'Somos un estudio de software de servicio completo especializado en aplicaciones web y móviles nativas de alto rendimiento. Desde el concepto inicial y el diseño UI/UX hasta el desarrollo full-stack y la integración en la nube, construimos productos digitales escalables adaptados a sus objetivos comerciales.'
                                    },
                                    {
                                        q: '¿Construyen tanto para iOS como para Android?',
                                        a: 'Sí. Nos especializamos en Desarrollo Nativo usando SwiftUI (iOS) y Kotlin (Android) para asegurar el mayor rendimiento, las animaciones más fluidas y la mejor experiencia de usuario. También desarrollamos plataformas web robustas para asegurar que su negocio sea accesible en todas partes.'
                                    },
                                    {
                                        q: '¿En qué industrias se especializan?',
                                        a: 'Aunque somos agnósticos en tecnología, tenemos una profunda experiencia en Hostelería (sistemas personalizados de gestión hotelera) y Marketplaces de Servicios. Nuestros proyectos actuales, como HotelApp y Nexfixer, muestran nuestra capacidad para manejar lógica de reserva compleja y plataformas de usuario multi-rol.'
                                    },
                                    {
                                        q: '¿Ofrecen servicios de diseño UI/UX?',
                                        a: 'Sí, el diseño es una parte fundamental de nuestro proceso. Creamos interfaces intuitivas centradas en el usuario y prototipos de alta fidelidad antes de comenzar cualquier desarrollo para asegurar que el producto final cumpla con su visión.'
                                    }
                                ]
                            },
                            {
                                title: '🚀 Proceso y Logística',
                                items: [
                                    {
                                        q: '¿Cuánto tiempo toma construir una app o sitio web?',
                                        a: 'Los plazos varían según la complejidad. Un MVP (Producto Mínimo Viable) estándar típicamente toma de 8 a 12 semanas. Sistemas más complejos, como una plataforma integrada de gestión hotelera, pueden tomar más tiempo. Proporcionamos una hoja de ruta detallada después de nuestra fase de descubrimiento inicial.'
                                    },
                                    {
                                        q: '¿Seré el dueño del código fuente después de terminar el proyecto?',
                                        a: 'Absolutamente. Una vez realizado el pago final, todos los derechos de propiedad intelectual y el código fuente se transfieren a usted. Creemos en la transparencia y en asegurar que nuestros clientes tengan el control total sobre sus activos digitales.'
                                    },
                                    {
                                        q: '¿Cómo nos comunicamos durante el proceso de desarrollo?',
                                        a: 'Priorizamos la transparencia. Tendrá un punto de contacto dedicado y actualizaciones regulares a través de su canal preferido (Slack, Telegram o Email). También proporcionamos demostraciones programadas al final de cada sprint de desarrollo para que pueda ver el progreso en tiempo real.'
                                    },
                                    {
                                        q: '¿Pueden trabajar con una base de código existente?',
                                        a: 'Definitivamente. Con frecuencia ayudamos a los clientes a escalar o refactorizar aplicaciones existentes. Después de una auditoría de código exhaustiva, podemos proporcionar un plan para la modernización, corrección de errores o expansión de funciones.'
                                    }
                                ]
                            },
                            {
                                title: '🔒 Seguridad y Soporte',
                                items: [
                                    {
                                        q: '¿Cómo manejan la seguridad de los datos?',
                                        a: 'La seguridad se integra en nuestro código desde el primer día. Usamos cifrado estándar de la industria, protocolos de API seguros e infraestructuras de nube confiables como Firebase o AWS para asegurar que sus datos de usuario y lógica de negocio estén protegidos contra vulnerabilidades.'
                                    },
                                    {
                                        q: '¿Ofrecen mantenimiento después del lanzamiento?',
                                        a: 'Sí. La tecnología avanza rápido, y no "desaparecemos" después del lanzamiento. Ofrecemos paquetes de mantenimiento continuo para asegurar que su app siga siendo compatible con las últimas actualizaciones de iOS/Android y para ayudarle a lanzar nuevas funciones a medida que su negocio crece.'
                                    },
                                    {
                                        q: '¿Dónde se alojarán mis datos y mi aplicación?',
                                        a: 'Típicamente recomendamos y configuramos el alojamiento en los principales proveedores de nube como AWS, Google Cloud o Firebase. Sin embargo, somos flexibles y podemos trabajar con su proveedor de alojamiento preferido o infraestructura local si es necesario.'
                                    }
                                ]
                            }
                        ]
                    },
                    tech: {
                        react: {
                            title: 'Desarrollo en React / Next.js',
                            desc: 'Construyendo aplicaciones web de alto rendimiento y amigables con el SEO con el último ecosistema de React.',
                            features: ['Renderizado del Lado del Servidor (SSR)', 'Optimización de Core Web Vitals', 'Arquitectura Basada en Componentes', 'Aplicaciones Web Progresivas (PWA)', 'Gestión de Estado Personalizada', 'Internacionalización (i18n)', 'Integración de Headless CMS', 'Estándares de Seguridad Empresarial'],
                            benefits: ['Rendimiento ultrarrápido', 'Excelente visibilidad SEO', 'Interfaz de usuario interactiva y responsiva', 'Código altamente mantenible', 'Flujo de datos complejos sin problemas', 'Alcance de mercado global', 'Gestión de contenido sin esfuerzo', 'Protección segura de datos de usuario'],
                            image: '/images/tech/react-hero.png'
                        },
                        node: {
                            title: 'Backend en Node.js',
                            desc: 'Sistemas backend escalables e impulsados por eventos con Node.js y NestJS.',
                            features: ['Arquitectura de Microservicios', 'WebSockets en Tiempo Real', 'Manejo de Alta Concurrencia', 'APIs REST y GraphQL', 'Orquestación de Microservicios', 'Flujos de Datos en Tiempo Real', 'Optimización de Consultas de Base de Datos', 'Suites de Pruebas Automatizadas'],
                            benefits: ['Baja latencia de respuesta', 'Ciclos de desarrollo rápidos', 'Escalado rentable', 'Integración robusta del ecosistema', 'Escalabilidad horizontal elástica', 'Notificaciones instantáneas al usuario', 'Recuperación de datos de alta velocidad', 'Entorno de producción sin errores'],
                            image: '/images/tech/node-hero.png'
                        },
                        ai: {
                            title: 'IA y Aprendizaje Automático',
                            desc: 'Aprovechando LLMs, redes neuronales y visión por computadora para automatizar procesos de negocio específicos.',
                            features: ['Entrenamiento de LLM Personalizados', 'Analítica Predictiva', 'Sistemas de Visión por Computadora', 'Procesamiento de Lenguaje Natural', 'Ajuste Fino de LLM Personalizados', 'Reconocimiento de Imagen y Video', 'Sistemas de Análisis de Sentimiento', 'Motores de Recomendación de IA'],
                            benefits: ['Toma de decisiones automatizada', 'Reducción de costos operativos', 'Personalización de usuario mejorada', 'Crecimiento empresarial basado en datos', 'Inteligencia específica de la industria', 'Inspección visual automatizada', 'Información profunda de los comentarios de los clientes', 'Personalización que impulsa las ventas'],
                            image: '/images/tech/ai-hero.png'
                        },
                        cloud: {
                            title: 'Infraestructura en la Nube',
                            desc: 'Arquitectura de nube segura y escalable en AWS, Google Cloud y Azure con orquestación de Kubernetes.',
                            features: ['Kubernetes y Docker', 'Computación Serverless', 'Pipelines de CI/CD', 'Infraestructura como Código', 'Balanceo de Carga Multi-región', 'Seguridad Gestionada y WAF', 'Serverless Rentable', 'Pipelines de CI/CD Automatizados'],
                            benefits: ['Despliegues sin tiempo de inactividad', 'Escalado elástico automático', 'Soluciones de recuperación ante desastres', 'Cumplimiento de seguridad de primer nivel', 'Alta disponibilidad y recuperación ante desastres', 'Protección contra amenazas de grado militar', 'Ahorros por pago por ejecución', 'Despliegue de código rápido y seguro'],
                            image: '/images/tech/cloud-hero.png'
                        }
                    }
                }
            }
        }
    });

export default i18n;
